import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction
} from 'mobx';
import { ZodError } from 'zod';
import type { DTO, Nullable, Writeable } from '@/type-utils';
import {
  InvalidArgumentError,
  InvalidOperationError,
  InvalidStateError,
  UnableToAccessResourceError
} from '@/utils/errors';
import ProductService from '@/services/isomorphic/ProductService';
import ReturnsLabelService, {
  type IReturnLabel,
  LabelOption
} from '@/services/isomorphic/ReturnsLabelService';
import type {
  BasicHistoricOrder,
  HistoricOrder,
  IHistoricOrder
} from '@/services/isomorphic/OrderLookupService/data-structures';
import ConfigurationService from '@/services/isomorphic/ConfigurationService';
import { EnvironmentService } from '@/services/isomorphic/EnvironmentService';
import LoggerService from '@/services/isomorphic/LoggerService';
import type { Country } from '@/constructs/Country';
import OrderLookupService from '@/services/isomorphic/OrderLookupService';
import type { Province } from '@/constructs/provinces/Province';
import type { InvalidReturnAddressError } from '@/services/isomorphic/ReturnsLabelService/errors';
import type { ILocale } from '@/constructs/LocaleSchema';
import { isNullish, isNullOrEmpty } from '@/utils/null-utils';
import Model from '../../Model';
import type { IAddressWithPhoneNumber } from '../../Address';
import { ReturnsFlowStateSchema } from './schemas';
import { type IReturnOrder, ReturnOrderType, ReturnReason } from '../order';
import ReturnItemType from '../order/item/ReturnItemType';
import { InvalidReturnsFlowStateDataError } from './InvalidReturnsFlowStateDataError';
import type IReturnsFlowState from './IReturnsFlowState';
import RETURNS_FLOW_STEP_ORDER from './RETURNS_FLOW_STEP_ORDER';
import ReturnsFlowStep from './ReturnsFlowStep';
import type { ReturnItem } from '../order/item';
import { ProductModel, ProductType } from '../../Product';
import type { IOrderLine } from '../../Order';
import { type IOrderHistory, OrderHistoryModel } from '../../OrderHistory';
import type { IReturnOrderLine } from './IReturnOrderLine';
import type { IReturnItemData } from '../order/item/IReturnItemBase';
import type IReturnableItem from './IReturnableItem';
import type { IExchangeItemData } from './IReturnableItem';

/** Represents a single returnable item in an order. */
export type ReturnOrderLine = IOrderLine & {
  /**
   * An identifier for the return order line.
   * This is necessary to distinguish between multiple quantities of the same SKU,
   * e.g. if a shipment contains 2 pairs of '1116737-BLK-10', but only one is being returned.
   */
  id: string;
};

/**
 * # ReturnsFlowStateModel.
 *
 * Represents a **Returns Flow State**, an object that manages the state of a returns flow.
 *
 * In a similar fashion to an [MVVM View Model](https://en.wikipedia.org/wiki/Model%E2%80%93view%E2%80%93viewmodel),
 * this Model acts like an abstraction layer; exposing properties and methods that facilitate
 * the interactions between the UI and the Returns Wizard Flow.
 *
 * However, unlike a View Model, this model is meant to handle the state for _all_ the returns flow views. The
 * Model to View relationship is one to many.
 *
 * ## Key Features.
 *
 * ### Session Storage Persistence.
 *
 * Being a Model, a `ReturnsFlowStateModel` can be turned into and constructed from an `IReturnsFlowState` DTO.
 * This is particularly useful for persisting the state of the Returns Flow in the session storage.
 * For instance, say a user wants to do an uneven exchange (i.e. Exchange an item for another item of a different model).
 * The user should be allowed to browse the page in Exchange Mode, in which the "Add to Cart" button is replaced for
 * an "Add to Exchange" button in Product Details pages. This would mean that the state of the Returns Flow must be
 * saved somewhere before leaving the Returns Wizard. Being able to turn this Model into a DTO, save it to the session storage, and
 * then restore it when needed fulfills that need perfectly.
 *
 * ### Easy reference and observability.
 *
 * The {@link ReturnsFlowStateModel.fromSessionStorage} static method and the {@link useReturnsFlowState} helper hook make it
 * easy to reference the Returns Flow saved to the session storage from anywhere.
 * Not to mention that `ReturnsFlowStateModel` is MobX-observable, which means that it can make UI components react to
 * state changes; making it a drop-in solution for reading and manipulating the Returns Flow state from the UI.
 *
 * ## How to Use.
 *
 * Important Note: The `ReturnsFlowStateModel` is meant to manage the state of a Returns Flow as described in this
 * [diagram](https://lucid.app/lucidchart/cfa7feaa-9c39-429f-9fe0-d8ebb6a86375/edit?viewport_loc=810%2C-86%2C2304%2C1183%2C0_0&invitationId=inv_b5793945-e31d-43d4-b1dc-75a752a1fff8).
 *
 * ### Step 1: Create a ReturnsFlowStateModel.
 *
 * You can obtain a new ReturnsFlowStateModel by either:
 *
 * - Calling the {@link ReturnsFlowStateModel.create create} static method.
 * - Calling the {@link ReturnsFlowStateModel.fromSessionStorage fromSessionStorage} static method, which will act exactly the same as
 *   the {@link ReturnsFlowStateModel.create create} method if no `IReturnsFlowState` DTO is found in the session storage.
 * - Using the {@link useReturnsFlowState} hook in a React component, which uses {@link ReturnsFlowStateModel.fromSessionStorage fromSessionStorage}
 *   under the hood.
 *
 * ### Step 2: Load an Order.
 *
 * Before being able to select which items to return, the original order must be loaded into the model.
 * For this, use the {@link ReturnsFlowStateModel.loadOrder loadOrder} method.
 *
 * ```ts
 * const returnsFlowState = useReturnsFlowState();
 *
 * const onSelectOrder = useCallback((order: IHistoricOrder) => {
 *    // useReturnsFlowState will return null if the data hasn't been loaded from session storage yet.
 *    if (returnsFlowState) {
 *        returnsFlowState.loadOrder(returnsFlowState);
 *    }
 * }, []);
 * ```.
 *
 * This step is usually done when the customer is presented a list of their recent orders and prompted to select
 * one to start the return from.
 *
 * ### Step 3: Select items to return.
 *
 * Now that the order has been loaded in, you must select the subset of its items that will be returned or
 * exchanged. For this, use the {@link ReturnsFlowStateModel.selectItemsToReturn selectItemsToReturn} method, which takes an
 * array with the SKUs of the items that will be selected for return.
 *
 * ```ts
 * const onSelectReturnItems = useCallback((returnItemSKUs: Array<string>) => {
 *    if (returnsFlowState) {
 *        returnsFlowState.selectItemsToReturn(returnItemSKUs);
 *    }
 * }, []);
 * ```.
 *
 * ### Step 4: Select a return type for each item to return.
 *
 * After selecting which items will be returned, a {@link ReturnItemType return type} must be selected for each one.
 *
 * #### Refunds and Even Exchanges.
 *
 * Use the {@link ReturnsFlowStateModel.currentItem currentItem} attribute to identify the item the selection is currently being
 * made for, and the {@link ReturnsFlowStateModel.selectItemReturnType selectItemReturnType} method to select the desired return type.
 *
 * If the desired exchange item is of a different variant (maybe the user wants a different color or size),
 * specify its SKU as the third parameter.
 *
 * ```ts
 * const onSelectRefund = useCallback((returnReason: ReturnReason) => {
 *    returnsFlowState.selectItemReturnType(ReturnItemType.Refund, returnReason);
 * }, []).
 *
 * const onSelectSameSKUExchange = useCallback((returnReason: ReturnReason) => {
 *    returnsFlowState.selectItemReturnType(ReturnItemType.SameSKUExchange, returnReason);
 * }, []).
 *
 * const onSelectSameModelExchange = useCallback((returnReason: ReturnReason, exchangeItemSKU: string) => {
 *    returnsFlowState.selectItemReturnType(ReturnItemType.SameSKUExchange, returnReason, exchangeItemSKU);
 * }, [])
 * ```
 *
 * #### Uneven Exchanges.
 *
 * The flow for uneven exchanges is a tad bit more complex. It looks like this:
 *
 * 1. Call the {@link ReturnsFlowStateModel.startExchange startExchange} to enable Exchange Mode.
 * 2. Redirect the customer to the page so they can select which product to receive in exchange.
 * 3. When the customer selects a product, call the {@link ReturnsFlowStateModel.completeExchange completeExchange} method,
 *    which will record the customer's selection and disable Exchange Mode.
 * 4. Redirect the customer back to the Returns Wizard.
 *
 * ```ts
 * const onSelectDifferentModelExchange = useCallback((returnReason: ReturnReason) => {
 *    // This return reason will be stored in the model until the exchange is completed.
 *    returnsFlowState.startExchange(returnReason);
 *
 *    // Redirect the customer to the "All" category. Exchange Mode will now be enabled.
 *    router.push("/c/all")
 * }, []);
 *
 *
 * // On the PDP...
 * const onAddToCart = useCallback((itemSKU: string) => {
 *    // Complete the exchange selection by passing the SKU of the selected item.
 *    // This will disable Exchange Mode.
 *    returnsFlowState.completeExchange(itemSKU, ReturnItemType.DifferentModelExchange);
 *
 *    // Redirect back to the Returns Wizard.
 *    router.push("/returns/select-return-type")
 * }, []);
 * ```
 *
 * ### Step 5: Review.
 *
 * When a return type has been selected for the last item, the Returns Wizard can now
 * progress to the Review step.
 *
 * By this point, the {@link ReturnsFlowStateModel.returnOrder returnOrder} attribute should now be complete.
 * You can double check this by making sure the {@link ReturnsFlowStateModel.isReturnOrderComplete isReturnOrderComplete} is `true`.
 *
 * This step does not require any input to the `ReturnsFlowStateModel`. The advised next
 * step from here is to send the {@link ReturnsFlowStateModel.returnOrder Return order} to the Returns Service if the customer
 * decides that everything is good to go.
 *
 *
 * ### Step 6: Confirmation.
 *
 * The return order has been sent. The customer will now see a confirmation page.
 * Congratulations! You carried out a Returns Flow all the way to the end!
 */
export default class ReturnsFlowStateModel
  extends Model<DTO<IReturnsFlowState>>
  implements IReturnsFlowState
{
  @observable private _currentStep: ReturnsFlowStep;

  @observable private _emailAddressOrOrderNumber: Nullable<string>;
  @observable private _billingZipCode: Nullable<string>;
  @observable private _orderHistory: Nullable<
    OrderHistoryModel<BasicHistoricOrder>
  >;

  @observable private _isOrderLoaded: boolean;
  @observable private _isExchangeModeActive: boolean;

  @observable private _returnOrder?: Nullable<Partial<Writeable<IReturnOrder>>>;
  @observable private _order?: Nullable<BasicHistoricOrder>;
  @observable private _orderItems?: Nullable<Array<IReturnOrderLine>>;
  @observable private _itemsSelectedForReturn?: Nullable<ReadonlyArray<string>>;
  @observable private _itemsPendingReturnTypeSelection?: Nullable<
    Array<string>
  >;

  @observable private _currentItemID?: Nullable<string>;
  @observable private _storedReturnReason?: Nullable<ReturnReason>;
  @observable private _labelOption?: LabelOption;

  @observable private _returnLabel?: IReturnLabel;

  @observable private _errorMessage: Nullable<string> = null;

  /**
   * Is the current returns label using a QR code image instead of the normal
   * shipping label.
   * @returns `true` if the label is a QR code, `false` otherwise.
   */
  public get isQRCode(): boolean {
    return this._labelOption === LabelOption.QRCode;
  }

  /**
   * The current error message that can be used to display an error related to the
   * ReturnsFlowStateModel. Once it is read it will be made null.
   * @returns A nullable string.
   */
  @action public getErrorMessage(): Nullable<string> {
    const currentMessage = this._errorMessage;
    this._errorMessage = null;

    return currentMessage;
  }

  /**
   * Evaluate if a provided {@link ReturnItemType return type} represents an even exchange.
   * @param returnType - A {@link ReturnItemType} value to evaluate.
   *
   * @returns
   * - `true` if the return type is an even exchange.
   * - `false` if it is an uneven exchange.
   * - `undefined` if the return type is not an exchange type at all.
   */
  private isEvenExchange(returnType: ReturnItemType): Nullable<boolean> {
    switch (returnType) {
      // The item will represent an even exchange if the type is a same sku or same model exchange.
      case ReturnItemType.SameSKUExchange:
      case ReturnItemType.SameModelExchange:
        return true;

      // Use `false` if the item is an uneven exchange.
      case ReturnItemType.DifferentModelExchange:
        return false;

      // If it is neither of these types, the item is not an exchange at all, so we return
      // `undefined` rather than `false` to denote this.
      default:
        return undefined;
    }
  }

  /**
   * Resolves the default {@link LabelOption} based on the returns configuration.
   *
   * @param locale - The user's {@link ILocale locale}, if not provided it will
   * use the default.
   * @returns The default {@link LabelOption}.
   */
  private static resolveDefaultLabelOption(locale?: ILocale): LabelOption {
    const { language, country } = locale ?? {};
    const config = ConfigurationService.getConfig('returns', language, country);
    const isQRCodeEnabled = config.getSetting('enableQRCodes').value;
    const isQRCodeDefault = config.getSetting('qrCodeDefault').value;

    if (isQRCodeEnabled && isQRCodeDefault) {
      return LabelOption.QRCode;
    }

    return LabelOption.ShippingLabel;
  }

  /**
   * Creates a new, uninitialized {@link ReturnsFlowStateModel}.
   *
   * Use this method when starting a returns flow from scratch.
   * If you want to use a DTO to restore existing returns flow data,
   * use the constructor or {@link ReturnsFlowStateModel.from} instead.
   *
   * @param locale - The user's {@link ILocale locale}, if not provided it will
   * use the default.
   * @returns A new, uninitialized {@link ReturnsFlowStateModel}.
   */
  public static create(locale?: ILocale): ReturnsFlowStateModel {
    return new ReturnsFlowStateModel({
      // Default step is the first step.
      currentStep: ReturnsFlowStep.OrderLookup,
      labelOption: ReturnsFlowStateModel.resolveDefaultLabelOption(locale),
      isOrderLoaded: false,
      haveItemsToReturnBeenSelected: false,
      isExchangeModeActive: false,
      isReturnTypeSelectionComplete: false,
      isReturnOrderComplete: false
    });
  }

  /**
   * Attempts to build a model from an existing {@link IReturnsFlowState} DTO
   * stored in the session storage.
   *
   * If no DTO is found, a new model will be {@link ReturnsFlowStateModel.create created}.
   * If a DTO is found but represents a completed return (Confirmation or TrackReturn step),
   * a new model will be created and saved to allow the user to start a fresh return.
   *
   * @param locale - The user's {@link ILocale locale}.
   * @returns A {@link ReturnsFlowStateModel}, either built from existing data in the session storage
   * if available, or a new one.
   */
  public static fromSessionStorage(locale: ILocale): ReturnsFlowStateModel {
    const dto = ReturnsLabelService.getFlowStateFromSessionStorage();

    // If DTO exists and is not at a completed step, restore the in-progress flow.
    if (dto) {
      const isCompletedFlow =
        dto.currentStep === ReturnsFlowStep.Confirmation ||
        dto.currentStep === ReturnsFlowStep.TrackReturn;

      // If the flow is completed, clear it and start fresh to allow a new return.
      if (isCompletedFlow) {
        const newModel = ReturnsFlowStateModel.create(locale);
        newModel.saveToSessionStorage();
        return newModel;
      }

      // Otherwise, restore the in-progress flow.
      return ReturnsFlowStateModel.from(dto);
    }

    // Otherwise, make a new model if not found.
    return ReturnsFlowStateModel.create(locale);
  }

  /**
   * Clears the existing {@link IReturnsFlowState} DTO stored in the session storage (if any)
   * by storing a fresh, new one.
   * @returns The newly created blank {@link ReturnsFlowStateModel}.
   */
  public static clearSessionFlowState(): ReturnsFlowStateModel {
    const newModel = ReturnsFlowStateModel.create();
    newModel.saveToSessionStorage();

    return newModel;
  }

  /**
   * Gets a URL for a provided step in the flow. Useful for
   * sending the user to a specific step in the flow.
   *
   * @param step - The {@link ReturnsFlowStep step} to go to.
   * @param error - An optional error message to append to the URL.
   * @returns A {@link URL} object.
   */
  public static getStepURL(step: ReturnsFlowStep, error?: string): URL {
    const { origin } = EnvironmentService.url;

    const baseFlowLocation =
      ConfigurationService.getConfig('returns').getSetting(
        'flow.baseLocation'
      ).value;

    // ReturnsFlowSteps are used directly in URLs
    const url = !isNullOrEmpty(error)
      ? `${baseFlowLocation}/${step}?error=${error}`
      : `${baseFlowLocation}/${step}`;
    return new URL(url, origin);
  }

  /**
   * For a given step, gets its number in the flow.
   *
   * @param step - The {@link ReturnsFlowStep step} to get the number for.
   * @returns The step number.
   */
  public static getStepNumber(step: ReturnsFlowStep): number {
    return RETURNS_FLOW_STEP_ORDER[step];
  }

  /** @inheritdoc */
  public constructor(dto: DTO<IReturnsFlowState>) {
    super(dto);

    try {
      const parsedDTO = ReturnsFlowStateSchema.parse(
        dto
      ) as DTO<IReturnsFlowState>;

      this._currentStep = parsedDTO.currentStep;

      this._isOrderLoaded = parsedDTO.isOrderLoaded;
      this._isExchangeModeActive = parsedDTO.isExchangeModeActive;
      this._returnOrder = parsedDTO.returnOrder as Nullable<
        Partial<IReturnOrder>
      >;

      this._order = parsedDTO.order;
      this._orderItems = parsedDTO.orderItems && [...parsedDTO.orderItems];
      this._orderHistory =
        parsedDTO.orderHistory &&
        OrderHistoryModel.from(
          parsedDTO.orderHistory as DTO<IOrderHistory<HistoricOrder>>
        );

      this._itemsSelectedForReturn = parsedDTO.itemsSelectedForReturn && [
        ...parsedDTO.itemsSelectedForReturn
      ];

      this._itemsPendingReturnTypeSelection =
        parsedDTO.itemsPendingReturnTypeSelection && [
          ...parsedDTO.itemsPendingReturnTypeSelection
        ];

      this._currentItemID = parsedDTO.currentItemID;

      this._labelOption = parsedDTO.labelOption ?? LabelOption.ShippingLabel;

      makeObservable(this);
    } catch (error) {
      if (error instanceof ZodError) {
        LoggerService.error(
          new InvalidReturnsFlowStateDataError(
            'Error constructing ReturnsFlowStateModel:' +
              ' The passed DTO does not satisfy the configured schema.',
            { cause: error }
          )
        );

        /**
         * When this fails we should abandon the state.
         * Going to order lookup (first step) will practically
         * reset the flow state.
         *
         * @see {@link resetFlowToStep}
         */
        this.goTo(ReturnsFlowStep.OrderLookup);

        // Assign required properties here in the constructor to appease the
        // Typescript gods.
        this._currentStep = ReturnsFlowStep.OrderLookup;
        this._isOrderLoaded = false;
        this._isExchangeModeActive = false;
      } else {
        throw error;
      }
    }
  }

  /**
   * Saves this flow state to session storage.
   */
  private saveToSessionStorage(): void {
    ReturnsLabelService.saveFlowStateToSessionStorage(this);
  }

  /**
   * Gets the default {@link LabelOption} from the returns configuration.
   * @returns The default label option.
   */
  private getDefaultLabelOption(): LabelOption {
    return ReturnsFlowStateModel.resolveDefaultLabelOption();
  }

  /**
   * Partially resets the flow up to the selected step by clearing any data
   * that should be entered from that step onwards.
   *
   * If a step is not specified, the {@link currentStep current step} will be used instead.
   *
   * @param step - The {@link ReturnsFlowStep step} to reset the flow to.
   *
   * @see The {@link clearSessionFlowState} method if you want to completely reset the flow.
   */
  @action public resetFlowToStep(step?: ReturnsFlowStep): void {
    const stepToResetTo = step ?? this.currentStep;
    const stepNumber = RETURNS_FLOW_STEP_ORDER[stepToResetTo];

    // Pattern-matching switch
    // Good resource on this: https://kyleshevlin.com/pattern-matching
    switch (true) {
      // If the step is OrderLookup or an earlier step...
      case stepNumber <= RETURNS_FLOW_STEP_ORDER[ReturnsFlowStep.OrderLookup]: {
        this._emailAddressOrOrderNumber = undefined;
        this._billingZipCode = undefined;
        this._orderHistory = undefined;
        this._labelOption = this.getDefaultLabelOption();

        // falls through
      }

      // If the step is OrderHistory or an earlier step...
      case stepNumber <=
        RETURNS_FLOW_STEP_ORDER[ReturnsFlowStep.OrderHistory]: {
        this._isOrderLoaded = false;
        this._order = undefined;
        this._orderItems = undefined;
        this._itemsSelectedForReturn = undefined;
        this._itemsPendingReturnTypeSelection = undefined;

        // falls through
      }

      // If the step is OrderDetails or an earlier step...
      case stepNumber <=
        RETURNS_FLOW_STEP_ORDER[ReturnsFlowStep.OrderDetails]: {
        this._currentItemID = undefined;
        this._returnOrder = undefined;
        this._returnLabel = undefined;

        // falls through
      }

      // If the step is ReturnTypeSelect or an earlier step...
      case (stepNumber ===
        RETURNS_FLOW_STEP_ORDER[ReturnsFlowStep.ReturnTypeSelect] &&
        !this.hasPreviousItem()) ||
        stepNumber <
          RETURNS_FLOW_STEP_ORDER[ReturnsFlowStep.ReturnTypeSelect]: {
        // Only reset if not popping an item from the return type
        // selection stack.
        this._isExchangeModeActive = false;
        this._storedReturnReason = undefined;

        // falls through
      }

      // If the step is Review or an earlier step...
      case stepNumber <= RETURNS_FLOW_STEP_ORDER[ReturnsFlowStep.Review]: {
        // Nothing here, but leaving the case for the future.
        // falls through
      }

      // If the step is Confirmation or an earlier step...
      case stepNumber <=
        RETURNS_FLOW_STEP_ORDER[ReturnsFlowStep.Confirmation]: {
        // Nothing here, but leaving the case for the future.
        // falls through
      }

      default:
        break;
    }
  }

  /**
   * Goes to the specified step.
   *
   * Will also:
   * - Trigger state changes on any observer components.
   * - {@link resetFlowToStep Reset the flow to the specified step}.
   *
   * @param step - The {@link ReturnsFlowStep step} to go to.
   */
  @action public goTo(step: ReturnsFlowStep): void {
    switch (step) {
      case ReturnsFlowStep.OrderLookup:
        break;

      case ReturnsFlowStep.OrderHistory:
        // Check that an order history has been loaded
        break;

      case ReturnsFlowStep.OrderDetails:
        // Check that an order has been loaded
        if (!this.isOrderLoaded) {
          this._currentStep = ReturnsFlowStep.OrderDetails; // prevent infinite loop
          this.goBack();
          return;
        }
        break;

      case ReturnsFlowStep.ReturnTypeSelect:
        if (this.isReturnTypeSelectionComplete) {
          this.previousItem();
        }

        break;

      case ReturnsFlowStep.Review:
        // Check that return order is complete
        break;

      case ReturnsFlowStep.Confirmation:
        // Check that return order is confirmed.
        break;

      default:
        break;
    }

    this.resetFlowToStep(step);
    this._currentStep = step;
    this.saveToSessionStorage();
  }

  /**
   * Goes back one step.
   *
   * Will also:
   * - Trigger state changes on any observer components.
   * - {@link resetFlowToStep Reset the flow to the new step}.
   */
  @action public goBack(): void {
    switch (this._currentStep) {
      case ReturnsFlowStep.OrderLookup:
        break;

      case ReturnsFlowStep.OrderHistory:
        this.goTo(ReturnsFlowStep.OrderLookup);
        break;

      case ReturnsFlowStep.OrderDetails:
        if (this._orderHistory) {
          this.goTo(ReturnsFlowStep.OrderHistory);
        } else {
          this.goTo(ReturnsFlowStep.OrderLookup);
        }
        break;

      case ReturnsFlowStep.ReturnTypeSelect:
        // If there is a previous item, go back to that item.
        if (this.hasPreviousItem()) {
          this.previousItem();
        } else {
          // If not, go back to the order details screen.
          this.goTo(ReturnsFlowStep.OrderDetails);
        }

        break;

      case ReturnsFlowStep.Review:
        this.goTo(ReturnsFlowStep.ReturnTypeSelect);
        break;

      case ReturnsFlowStep.Confirmation:
        this.goTo(ReturnsFlowStep.Review);
        break;

      default:
        break;
    }
  }

  /**
   * Gets a URL for the current step in the flow. Useful for
   * coming back to the returns flow after leaving temporarily.
   *
   * @returns A {@link URL} object.
   */
  public getCurrentStepURL(): URL {
    return ReturnsFlowStateModel.getStepURL(this._currentStep);
  }

  /**
   * Determines if there is a next item in the
   * {@link ReturnsFlowStateModel.itemsPendingReturnTypeSelection `itemsPendingReturnTypeSelection`} array.
   *
   * @returns A value of `true` if there is indeed a next item in the array.
   *
   * @throws An {@link InvalidStateError} if an order hasn't been loaded to the Model yet, or if the items
   * to return haven't been selected yet.
   */
  public hasNextItem(): boolean {
    if (!this._isOrderLoaded || !this._itemsPendingReturnTypeSelection) {
      throw new InvalidStateError(
        'Cannot determine if there is a next item. Please load an order into the model and select the items to return first.'
      );
    }

    if (this._itemsPendingReturnTypeSelection.length <= 0) {
      return false;
    }

    return true;
  }

  /**
   * Gets the count of items pending return type selection.
   * @returns The count of items pending return type selection.
   */
  private get itemsPendingReturnTypeSelectionCount(): number {
    if (
      !this._isOrderLoaded ||
      !this._itemsPendingReturnTypeSelection ||
      !this._itemsSelectedForReturn
    ) {
      throw new InvalidStateError(
        'Cannot determine if there is a previous item. Please load an order into the model and select the items to return first.'
      );
    }

    let pendingItems = this._itemsPendingReturnTypeSelection.length;

    // Add one to account for the current item if present.
    if (!isNullOrEmpty(this.currentItem)) {
      pendingItems += 1;
    }

    return pendingItems;
  }

  /**
   * Determines if there is a previous item.
   *
   * @returns A value of `true` if there is indeed a next item in the array.
   *
   * @throws An {@link InvalidStateError} if an order hasn't been loaded to the Model yet, or if the items
   * to return haven't been selected yet.
   */
  public hasPreviousItem(): boolean {
    if (
      !this._isOrderLoaded ||
      !this._itemsPendingReturnTypeSelection ||
      !this._itemsSelectedForReturn
    ) {
      throw new InvalidStateError(
        'Cannot determine if there is a previous item. Please load an order into the model and select the items to return first.'
      );
    }

    // Add one to account for the current item.
    const pendingItems = this.itemsPendingReturnTypeSelectionCount;
    const allItems = this._itemsSelectedForReturn.length;

    if (pendingItems < allItems) {
      return true;
    }

    return false;
  }

  /**
   * Returns the next item's SKU in the
   * {@link ReturnsFlowStateModel.itemsPendingReturnTypeSelection `itemsPendingReturnTypeSelection`} array,
   * or `null` if there is no next item.
   *
   * This method automatically saves the updated flow state to the session storage.
   *
   * @returns The next item's SKU, or `null`.
   *
   * @throws An {@link InvalidStateError} if an order hasn't been loaded to the Model yet, or if the items
   * to return haven't been selected yet.
   */
  @action public nextItem(): Nullable<string> {
    if (!this._itemsPendingReturnTypeSelection) {
      throw new InvalidOperationError(
        'Cannot get the next item. Please load an order into the model and select the items to return first.'
      );
    }

    if (this.hasNextItem()) {
      this._currentItemID =
        this._itemsPendingReturnTypeSelection.shift() as string;
    } else {
      this._currentItemID = null;

      // Manually set step to Review since return type selection is complete.
      this._currentStep = ReturnsFlowStep.Review;
    }

    this.saveToSessionStorage();
    return this._currentItemID;
  }

  /**
   * Returns the previous item.
   *
   * This method automatically saves the updated flow state to the session storage.
   *
   * @returns The next item's SKU, or `null`.
   *
   * @throws An {@link InvalidStateError} if an order hasn't been loaded to the Model yet, or if the items
   * to return haven't been selected yet.
   */
  @action public previousItem(): Nullable<string> {
    if (
      !this._itemsSelectedForReturn ||
      !this._itemsPendingReturnTypeSelection
    ) {
      throw new InvalidOperationError(
        'Cannot get the next item. Please load an order into the model and select the items to return first.'
      );
    }

    if (this.hasPreviousItem()) {
      const originalItemCount = this._itemsSelectedForReturn.length;
      const pendingItemCount = this.itemsPendingReturnTypeSelectionCount;

      const previousItemIdx = originalItemCount - pendingItemCount - 1;

      // If there is a current item already, save add it back at the head of
      // the pending items array.
      if (!isNullOrEmpty(this._currentItemID)) {
        this._itemsPendingReturnTypeSelection = [
          this._currentItemID,
          ...this._itemsPendingReturnTypeSelection
        ];
      }

      this._currentItemID = this._itemsSelectedForReturn[previousItemIdx];

      this._returnOrder?.returnItems?.pop();
    }

    this.saveToSessionStorage();
    return this._currentItemID;
  }

  /**
   * Loads an order history into the flow state to allow the customer
   * to select which one to start a return for.
   *
   * This step is optional; you can directly load an order to the
   * flow state.
   *
   * @param orderHistory - The {@link IOrderHistory order history} to load.
   */
  @action public loadOrderHistory(
    orderHistory: OrderHistoryModel<BasicHistoricOrder>
  ): void {
    this._orderHistory = orderHistory;
    this.goTo(ReturnsFlowStep.OrderHistory);
  }

  /**
   * Loads an order into the flow state.
   * **This step is required before selecting the items to return**.
   *
   * @param order - The order to load.
   * @throws An {@link InvalidArgumentError} if the specified order has not been delivered yet.
   */
  @action public loadOrder(order: HistoricOrder): void {
    const { orderID, shipments } = order;

    if (!OrderLookupService.isOrderReturnable(order)) {
      throw new InvalidArgumentError(
        `Cannot load non-returnable order "${orderID}" into the returns flow state model.`
      );
    }

    // TODO: Break down this card so it represents shipments. The order itself has no status.
    const { status } = order.shipments[0];

    // Flatten all items from all shipments
    const orderItems: Array<IReturnOrderLine> = [];
    for (const shipment of shipments) {
      for (const item of shipment.items) {
        for (let count = 0; count < item.quantity; count += 1) {
          orderItems.push({
            id: `${item.uuid}#${count}`,
            sourceOrderLine: item
          });
        }
      }
    }

    this._order = order;
    this._orderItems = orderItems;
    this._itemsPendingReturnTypeSelection = orderItems.map((i) => i.id);
    this._itemsSelectedForReturn = [];

    this._isOrderLoaded = true;
    this.goTo(ReturnsFlowStep.OrderDetails);
  }

  /**
   * Selects (by return order line ID) the subset of the loaded order's items that
   * will be returned.
   *
   * **This is the second required step**, but you must first
   * {@link ReturnsFlowStateModel.loadOrder load an order} into the flow for it to succeed.
   *
   * @param returnLineIds - The SKUs of the items that are going to be returned.
   *
   * @throws An {@link InvalidStateError} if an order hasn't been loaded in.
   * @throws An {@link InvalidArgumentError} if a passed SKU is not found in the order's items.
   */
  @action public selectItemsToReturn(
    returnLineIds: ReadonlyArray<string>
  ): void {
    if (!this._orderItems || !this._order) {
      throw new InvalidStateError(
        'Cannot select return items for order. Please initialize the return order first.'
      );
    }

    for (const id of returnLineIds) {
      const returnLine = this._orderItems.find((item) => item.id === id);
      if (!returnLine) {
        throw new InvalidArgumentError(
          `Cannot select return item with ID "${id}" to return in order "${this._order?.orderID}" since it was not found in the order's item list.`
        );
      }

      if (!returnLine.sourceOrderLine.isReturnable) {
        throw new InvalidArgumentError(
          `Cannot select return item with ID "${id}" to return in order "${this._order?.orderID}" since it is not returnable.`
        );
      }
    }

    const { orderID, email, orderDate } = this._order;

    if (!orderID || !email || isNullish(orderDate)) {
      throw new InvalidStateError(
        `Cannot initialize return order. The loaded order is missing required information.`
      );
    }

    // If the execution reaches this point, all of the items exist
    // in the order.
    this._itemsSelectedForReturn = returnLineIds;
    this._itemsPendingReturnTypeSelection = [...returnLineIds];

    // Initialize the return order.
    this._returnOrder = {
      originalOrderID: orderID,
      returnItems: [],
      customerEmail: email,
      orderPlacedDate: orderDate.toString(),
      shippingAddress: this.prefillShippingAddress()
    };

    this.nextItem();
  }

  /**
   * Prefills the shipping address for the return order. If the config
   * `showShippingAddress` is set to `false` the default shipping address will be
   * used instead of the order shipping address.
   * @returns The pre-filled shipping address.
   */
  private prefillShippingAddress(): Nullable<IAddressWithPhoneNumber> {
    const returnsConfig = ConfigurationService.getConfig('returns');

    /** Whether to show the shipping address to the end-user and allow them to edit it. */
    const showShippingAddress = returnsConfig.getSetting(
      'showShippingAddress'
    ).value;

    /** The fallback shipping address to use if the user should not choose a shipping address. */
    const defaultShippingAddress = returnsConfig.getSetting(
      'defaultShippingAddress'
    ).value;

    // If we're disabling the ability for the customer to see, enter, or edit the shipping
    // address, then fallback to a pre-defined address. This is typically the warehouse
    // address.
    if (!showShippingAddress) {
      return {
        addressLine1: defaultShippingAddress.street.value,
        city: defaultShippingAddress.city.value,
        country: defaultShippingAddress.country.value as Country,
        stateProvince: defaultShippingAddress.state.value as Province,
        zipPostalCode: defaultShippingAddress.zipCode.value,
        phoneNumber: defaultShippingAddress.phoneNumber.value,
        firstName: defaultShippingAddress.firstName.value,
        lastName: defaultShippingAddress.lastName.value
      };
    }

    /**
     * As `shippingAddress` is a nullable field, we can cast the type to the
     * order object to be a `HistoricOrder` and we can have access to the first best
     * shipping address there.
     *
     * If the order is a `MaskedHistoricOrder` then the shipping address will be null,
     * no problem, we can fill in that through the UI form later.
     */
    const order = this._order as IHistoricOrder;

    return order.shipments[0].shippingAddress;
  }

  /**
   * Selects a return type for the {@link ReturnsFlowStateModel.currentItem current item}.
   *
   * Only the return reason has to be provided for `Refund`
   * and `SameSKUExchange` return types.
   *
   * After recording the selection, the model will automatically
   * proceed to the {@link ReturnsFlowStateModel.nextItem next item}.
   *
   * @param returnType - The type of this return. Must be a member of the {@link ReturnItemType} enum.
   * @param returnReason - The reason for this return. Must be a member of the {@link ReturnReason} enum.
   */
  public async selectItemReturnType(
    returnType: ReturnItemType.Refund,
    returnReason: ReturnReason
  ): Promise<void>;

  /**
   * Selects a return type for the {@link ReturnsFlowStateModel.currentItem current item}.
   *
   * The `exchangeReturnLineID` parameter must be provided for exchange return types (with
   * the exception of `SameSKUExchange`).
   *
   * After recording the selection, the model will automatically
   * proceed to the {@link ReturnsFlowStateModel.nextItem next item}.
   *
   * @param returnType - The type of this return. Must be a member of the {@link ReturnItemType} enum.
   * @param returnReason - The reason for this return. Must be a member of the {@link ReturnReason} enum.
   * @param [exchangeItemSKU] - For exchanges, the SKU of the item to be sent in exchange.
   */
  public async selectItemReturnType(
    returnType: ReturnItemType,
    returnReason: ReturnReason,
    exchangeItemSKU: string
  ): Promise<void>;

  /**
   * Selects a return type for the {@link ReturnsFlowStateModel.currentItem current item}.
   *
   * Only the return reason has to be provided for `Refund` and `SameSKUExchange` return types.
   * The `exchangeItemSKU` parameter must be provided for exchange return types (with the
   * exception of `SameSKUExchange`).
   *
   * After recording the selection, the model will automatically
   * proceed to the {@link ReturnsFlowStateModel.nextItem next item}.
   *
   * @param returnType - The type of this return. Must be a member of the {@link ReturnItemType} enum.
   * @param returnReason - The reason for this return. Must be a member of the {@link ReturnReason} enum.
   * @param [exchangeItemSKU] - For exchanges, the SKU of the item to be sent in exchange.
   *
   * @throws An {@link InvalidStateError} if this method gets called before meeting all of its prerequisites.
   * @throws An {@link InvalidArgumentError} if no `exchangeItemSKU` is supplied for returns types that require it.
   */
  @action public async selectItemReturnType(
    returnType: ReturnItemType,
    returnReason: ReturnReason,
    exchangeItemSKU?: string
  ): Promise<void> {
    const { currentItem } = this;

    if (isNullish(currentItem)) {
      throw new InvalidStateError(
        `Cannot select return type since there is no current item.`
      );
    }

    if (!this._returnOrder?.returnItems) {
      throw new InvalidStateError(
        `Cannot select return type for item "${this._currentItemID}". Please initialize the return order first.`
      );
    }

    if (
      !this._itemsSelectedForReturn ||
      !this._itemsPendingReturnTypeSelection
    ) {
      throw new InvalidStateError(
        `Cannot select return type for item "${this._currentItemID}". Please select the items to return first.`
      );
    }

    if (isNullish(exchangeItemSKU) && returnType !== ReturnItemType.Refund) {
      throw new InvalidArgumentError(
        'Exchange Item SKU was not provided. It can only be skipped when making Refund return types.'
      );
    }

    const currentItemSKU = currentItem.sourceOrderLine.sku;

    const { data: item, exchangeData } = await this.getReturnItemDataFromSKU(
      currentItemSKU,
      exchangeItemSKU
    );

    // Push the return options to the return order.
    this._returnOrder.returnItems.push({
      // #region Minimum required return item data
      itemSKU: item.itemSKU,
      itemUPC: item.itemUPC,
      id: currentItem.id,
      displayName: item.displayName,
      imageUrl: item.imageUrl,
      color: item.color,
      // #endregion
      // #region Return item metadata
      isEvenExchange: this.isEvenExchange(returnType),
      returnType,
      returnReason,
      exchangeItemSKU,
      exchangeItemUPC: exchangeData?.upc,
      exchangeItemPrice: exchangeData?.price
      // #endregion
    });

    this._isExchangeModeActive = false;
    this._storedReturnReason = null;
    this.nextItem();
  }

  /**
   * Gets an `itemUPC` and `exchangeItemUPC` pair. In order to place returns
   * in AWS we need to provide the UPC code for each product in returns.
   *
   * @param itemSKU - The current item SKU.
   * @param exchangeItemSKU - The exchange item SKU.
   * @throws {@link RequestError} When cannot fetch the products based on SKU.
   * @returns - An object containing `itemUPC` and `exchangeItemUPC`.
   */
  private async getReturnItemDataFromSKU(
    itemSKU: string,
    exchangeItemSKU?: string
  ): Promise<IReturnableItem> {
    const productPromises: [
      Promise<ProductModel<ProductType>>,
      Promise<ProductModel<ProductType>> | undefined
    ] = [
      ProductService.getProduct(itemSKU),
      isNullOrEmpty(exchangeItemSKU)
        ? undefined
        : ProductService.getProduct(exchangeItemSKU)
    ];

    const [currentProduct, exchangeProduct] =
      await Promise.allSettled(productPromises);

    if (currentProduct.status === 'rejected') {
      throw new UnableToAccessResourceError(
        `Cannot retrieve current item product for the SKU: "${itemSKU}" to get UPC code.`,
        { cause: currentProduct.reason }
      );
    }

    if (exchangeProduct.status === 'rejected') {
      throw new UnableToAccessResourceError(
        `Cannot retrieve exchange item product for the SKU: "${exchangeItemSKU}" to get UPC code.`,
        { cause: exchangeProduct.reason }
      );
    }

    let exchangeData: IExchangeItemData | undefined;

    if (!isNullish(exchangeProduct.value)) {
      if (isNullish(exchangeProduct.value.price)) {
        throw new InvalidStateError(
          `The selected exchange product with SKU ${exchangeItemSKU} doesn't have a price.`
        );
      }

      exchangeData = {
        upc: exchangeProduct.value.upc,
        price: Number(exchangeProduct.value.price.price.amount)
      };
    }

    // Minimal data necessary to request a return
    const data: IReturnItemData = {
      id: currentProduct.value.id,
      itemSKU: currentProduct.value.sku,
      itemUPC: currentProduct.value.upc ?? '',
      displayName: currentProduct.value.name,
      imageUrl: currentProduct.value.primaryImage.src,
      color: currentProduct.value.defaultColor
    };

    return { data, exchangeData };
  }

  /**
   * Starts an exchange by enabling exchange mode and storing a return reason
   * in the model. The item to be exchanged will be the {@link IReturnsFlowState.currentItem current item}.
   *
   * To complete the exchange, call {@link ReturnsFlowStateModel.completeExchange the complete exchange method}.
   *
   * @param returnReasonToStore - The {@link ReturnReason return reason} to store in the model, which will
   * be the reason of the resulting {@link ReturnItem} return item.
   */
  @action public startExchange(returnReasonToStore: ReturnReason): void {
    this._storedReturnReason = returnReasonToStore;
    this._isExchangeModeActive = true;

    this.saveToSessionStorage();
  }

  /**
   * Aborts an exchange by disabling exchange mode and clearing any stored data
   * related to the active exchange from the model. The {@link IReturnsFlowState.currentItem current item} will not be changed.
   */
  @action public abortExchange(): void {
    this._storedReturnReason = null;
    this._isExchangeModeActive = false;

    this.saveToSessionStorage();
  }

  /**
   * Completes an exchange by disabling exchange mode and saving the
   * return item selection.
   *
   * Be sure to call the start exchange method first.
   *
   * @param exchangeItemSKU - The variant that the customer selected to receive in exchange.
   * @param returnType - The type of this return. Must be a member of the {@link ReturnItemType} enum.
   * Defaults to `ReturnItemType.DifferentModelExchange`.
   *
   * @throws An {@link InvalidStateError} if this method gets called before meeting all of its prerequisites.
   */
  public async completeExchange(
    exchangeItemSKU: string,
    returnType: ReturnItemType = ReturnItemType.DifferentModelExchange
  ): Promise<void> {
    if (!this.isExchangeModeActive) {
      throw new InvalidStateError(
        'Cannot complete item exchange: exchange mode is not enabled. Please make sure to call `startExchange` before calling `completeExchange`.'
      );
    }

    if (isNullish(this._storedReturnReason)) {
      throw new InvalidStateError(
        'Cannot complete item exchange: no stored return reason was found. Please make sure to call `startExchange` before calling `completeExchange`.'
      );
    }

    await this.selectItemReturnType(
      returnType,
      this._storedReturnReason,
      exchangeItemSKU
    );
  }

  /**
   * Evaluates if the {@link IReturnOrder return order} being built by this flow state
   * is {@link IReturnsFlowState.isReturnOrderComplete complete} already and returns it if so.
   *
   * If the return order is not complete yet, it will throw.
   *
   * @returns The {@link IReturnOrder return order} if complete.
   * @throws An {@link InvalidStateError} if the return order is not complete yet.
   *
   * @see The {@link ReturnsFlowStateModel.tryGetCompleteReturnOrder try-get version} of this method for a non-throwing alternative.
   */
  public getCompleteReturnOrder(): IReturnOrder {
    if (this.isReturnOrderComplete) {
      return this._returnOrder as IReturnOrder;
    }

    throw new InvalidStateError(
      `Cannot get complete Return Order: The current order doesn't have all the data necessary to be considered complete.`
    );
  }

  /**
   * Retrieves the {@link IReturnLabel return label} preventing it from being created multiple times.
   *
   * @returns The return label.
   *
   * @throws An {@link InvalidStateError} when the `labelOption` in `flowState` is
   * undefined, or a required field is missing.
   *
   * @throws An {@link InvalidReturnAddressError} when the address stored in the
   * flow state is rejected by the {@link ReturnsLabelService.getLabel `ReturnsLabelService`}.
   */
  public async getReturnLabel(): Promise<IReturnLabel> {
    if (isNullish(this._labelOption)) {
      throw new InvalidStateError(
        'Cannot get return label without having a label option defined'
      );
    }

    if (
      !this._order ||
      isNullOrEmpty(this._order.orderID) ||
      isNullOrEmpty(this._returnOrder?.originalOrderID) ||
      !this._returnOrder?.shippingAddress ||
      isNullOrEmpty(this._returnOrder?.customerEmail)
    ) {
      throw new InvalidStateError(
        'Cannot get return label without having order id, order email and shipping address defined'
      );
    }

    if (this._returnLabel) {
      return this._returnLabel;
    }

    // This can throw an `InvalidReturnAddressError`
    const returnLabel = await ReturnsLabelService.getLabel(
      // It's safe to cast here, since if we reached this point the return order is complete.
      this._returnOrder as IReturnOrder,
      this.isQRCode
    );

    this._returnLabel = returnLabel;

    return this._returnLabel;
  }

  /**
   * Evaluates if the {@link IReturnOrder return order} being built by this flow state
   * is {@link IReturnsFlowState.isReturnOrderComplete complete} already and returns it if so.
   *
   * If the return order is not complete yet, it return a `null` value.
   *
   * @returns The {@link IReturnOrder return order} if complete, or `null` if not.
   *
   * @see The {@link ReturnsFlowStateModel.getCompleteReturnOrder get version} of this method for an execution-stopping alternative.
   */
  public tryGetCompleteReturnOrder(): Nullable<IReturnOrder> {
    if (this.isReturnOrderComplete) {
      return this._returnOrder as IReturnOrder;
    }

    return null;
  }

  /** @inheritdoc */
  @computed public get currentStep(): ReturnsFlowStep {
    return this._currentStep;
  }

  /** @inheritdoc */
  @computed public get emailAddressOrOrderNumber(): Nullable<string> {
    return this._emailAddressOrOrderNumber;
  }

  /** @inheritdoc */
  @computed public get billingZipCode(): Nullable<string> {
    return this._billingZipCode;
  }

  /** @inheritdoc */
  @computed public get orderHistory(): Nullable<
    OrderHistoryModel<BasicHistoricOrder>
  > {
    return this._orderHistory;
  }

  /** @inheritdoc */
  @computed public get isOrderLoaded(): boolean {
    return this._isOrderLoaded;
  }

  /** @inheritdoc */
  @computed public get haveItemsToReturnBeenSelected(): boolean {
    return Boolean(this._itemsSelectedForReturn?.length);
  }

  /** @inheritdoc */
  @computed public get isExchangeModeActive(): boolean {
    return this._isExchangeModeActive;
  }

  /** @inheritdoc */
  @computed public get isReturnTypeSelectionComplete(): boolean {
    // Return type selection is complete if...
    if (
      // An order has been loaded
      this._isOrderLoaded &&
      // The items to return have been selected
      this.haveItemsToReturnBeenSelected &&
      // And there are no more items pending return type selection.
      this.itemsPendingReturnTypeSelectionCount === 0
    ) {
      return true;
    }

    // Otherwise, it's not complete.
    return false;
  }

  /** @inheritdoc */
  @computed public get isReturnOrderComplete(): boolean {
    if (
      !this._returnOrder ||
      !this.isOrderLoaded ||
      !this.isReturnTypeSelectionComplete
    ) {
      return false;
    }

    const { originalOrderID, returnItems, shippingAddress } = this._returnOrder;

    if (
      isNullOrEmpty(originalOrderID) ||
      !returnItems ||
      !shippingAddress ||
      isNullOrEmpty(this._labelOption)
    ) {
      return false;
    }

    if (returnItems.length !== this._itemsSelectedForReturn?.length) {
      return false;
    }

    return true;
  }

  /** @inheritdoc */
  @computed public get returnOrder(): Nullable<
    Partial<Writeable<IReturnOrder>>
  > {
    // Check if the return order has items
    if (this._returnOrder?.returnItems) {
      // Default the return ORDER type to Refund.
      let returnOrderType: ReturnOrderType = ReturnOrderType.Refund;

      for (const item of this._returnOrder.returnItems) {
        // But, if there is a single non-refund (i.e. exchange) return item in the order...
        if (item?.returnType !== ReturnItemType.Refund) {
          // Set the type for the whole order as Exchange.
          returnOrderType = ReturnOrderType.Exchange;
          break;
        }
      }
      runInAction(() => {
        this._returnOrder = { ...this._returnOrder, type: returnOrderType };
      });
    }

    return this._returnOrder;
  }

  /** @inheritdoc */
  @computed public get order(): Nullable<BasicHistoricOrder> {
    return this._order;
  }

  /** @inheritdoc */
  @computed public get orderItems(): Nullable<ReadonlyArray<IReturnOrderLine>> {
    return this._orderItems;
  }

  /** @inheritdoc */
  @computed public get itemsSelectedForReturn(): Nullable<
    ReadonlyArray<string>
  > {
    return this._itemsSelectedForReturn;
  }

  /** @inheritdoc */
  @computed public get itemsPendingReturnTypeSelection(): Nullable<
    ReadonlyArray<string>
  > {
    return this._itemsPendingReturnTypeSelection;
  }

  /** @inheritdoc */
  @computed public get currentItemID(): Nullable<string> {
    return this._currentItemID;
  }

  /** @inheritdoc */
  @computed public get currentItem(): Nullable<IReturnOrderLine> {
    return this.orderItems?.find((item) => item.id === this._currentItemID);
  }

  /** @inheritdoc */
  @computed public get labelOption(): LabelOption {
    if (isNullOrEmpty(this._labelOption)) {
      throw new InvalidStateError(
        'Label option should be set on creation or in the constructor.'
      );
    }

    return this._labelOption;
  }

  /** @inheritdoc */
  @action public setLabelOption(option: LabelOption): void {
    this._labelOption = option;
  }

  /** @inheritdoc */
  public setReturnOrderID(orderID: string): void {
    if (!this._returnOrder) {
      throw new InvalidStateError(
        'Return order should be defined to set an orderID.'
      );
    }

    this._returnOrder.orderID = orderID;
  }

  /**
   * When the order is masked, we can use this method to set the shipping address through the UI form.
   * @param address - The shipping address to set.
   * @throws An {@link InvalidStateError} if the return order is not defined.
   */
  public setShippingAddress(address: IAddressWithPhoneNumber): void {
    if (!this._returnOrder) {
      throw new InvalidStateError(
        'Return order should be defined to set a shipping address.'
      );
    }

    this._returnOrder.shippingAddress = address;
  }

  /**
   * Returns to the beginning of the returns flow and sets
   * the error message to be displayed to the user.
   * @param error - A string that can be displayed as an error.
   */
  public returnToStartWithError(error: string): void {
    this._errorMessage = error;
    this.goTo(ReturnsFlowStep.OrderLookup);
  }

  /** @inheritdoc */
  public toDTO(): DTO<IReturnsFlowState> {
    return {
      currentStep: this.currentStep,
      isOrderLoaded: this.isOrderLoaded,
      haveItemsToReturnBeenSelected: this.haveItemsToReturnBeenSelected,
      isExchangeModeActive: this.isExchangeModeActive,
      isReturnTypeSelectionComplete: this.isReturnTypeSelectionComplete,
      isReturnOrderComplete: this.isReturnOrderComplete,
      returnOrder: this.returnOrder,
      order: this.order,
      orderHistory: this.orderHistory?.toDTO(),
      orderItems: this.orderItems,
      itemsSelectedForReturn: this.itemsSelectedForReturn,
      itemsPendingReturnTypeSelection: this.itemsPendingReturnTypeSelection,
      currentItemID: this._currentItemID,
      labelOption: this._labelOption
    };
  }
}
